// shared-lib/kitchen-assembly/cabinetLayout/shallowRuns.js
//
// SHALLOW STORAGE as runs of its own (user ruling 2026-10-22, "shallow storage
// as 12" runs"). The generator decides where a slim run fits a walkway that a
// 24" one would pinch (`addShallowRuns.js`) and stamps its pieces
// `category.shallowRun`. Until 2026-10-23 no run owned them: they were kept
// out of their wall's run because, as that run's bays, the first edit rebuilt
// them at its 24" depth straight into the walkway (2026-09-13). So nothing
// rebuilt them, a run edit missed them, and a stretch that shared its wall
// with the deep run got no countertop at all (47B8DEE5's kitchen).
//
// Now each shallow STRETCH — the pieces that abut along one wall — is its own
// run at their depth (`run.depth`, which the fill builds at), with no uppers
// (the generator hangs none over one). A wall can therefore carry two runs:
// the deep one and the shallow one beside it. Ownership tells them apart by
// the piece (a shallow piece is a shallow run's) and, where one wall holds
// several of a kind, by which run's segment the piece stands in.
//
// Pure.

const { getWallAxes } = require('../wallMath');
const { footprintFor } = require('../../geometry/objectFootprint');

// Named for its stretch, so it answers to no wall's run id (`runIds.js`).
const { SHALLOW_RUN_ID_PREFIX } = require('../../cabinet-runs/runIds');
// Pieces this close along the wall are one stretch: the builder lays a
// stretch's boxes edge to edge.
const ABUT = 0.001;

/** A shallow storage run's piece — the generator's, or the engine's for it. */
const isShallowRunPiece = (obj) => obj?.category?.shallowRun === true;

/** A run this module derived for a shallow stretch. */
const isShallowStorageRun = (run) =>
  String(run?.id || '').startsWith(SHALLOW_RUN_ID_PREFIX);

/** The one wall a piece stands on: its cabinet-wall ref, else its parent. */
function wallIdOf(obj) {
  const refs = obj?.cabinetWallRefs;
  if (Array.isArray(refs) && refs.length) return refs[0]?.id || null;
  return (obj?.parentWallIds || [])[0] || null;
}

/** The piece's span along a wall, in that wall's params. */
function spanOn(obj, axes) {
  const c = obj?.center;
  const w = obj?.dimensions?.x;
  const d = obj?.dimensions?.z;
  if (!c || !(w > 0) || !(d > 0)) return null;
  const params = footprintFor(
    { x: c.x, z: c.z },
    w,
    d,
    obj.rotation?.y || 0
  ).map(
    (p) =>
      (p.x - axes.start.x) * axes.wallDir.x +
      (p.z - axes.start.z) * axes.wallDir.z
  );
  return { lo: Math.min(...params), hi: Math.max(...params) };
}

/**
 * One run per shallow stretch, on the walls `resolve` gives (the frame the
 * caller derives runs in).
 *
 * @param {Object[]} objects the layout's objects
 * @param {(ref: {id: string, kind: string}) => Object|null} resolve
 * @returns {Object[]} runs: `{ id, source, depth, uppers, segments, bays }`
 */
function shallowStorageRuns(objects, resolve) {
  const byWall = new Map();
  for (const o of objects || []) {
    if (o?.type !== 'lower-cabinet' || !isShallowRunPiece(o)) continue;
    const wallId = wallIdOf(o);
    if (!wallId) continue;
    if (!byWall.has(wallId)) byWall.set(wallId, []);
    byWall.get(wallId).push(o);
  }
  const runs = [];
  for (const [wallId, pieces] of byWall) {
    const axes = getWallAxes(resolve({ id: wallId, kind: 'real' }));
    if (!axes) continue;
    const spans = pieces
      .map((o) => ({ ...spanOn(o, axes), depth: o.dimensions.z }))
      .filter((s) => Number.isFinite(s.lo))
      .sort((a, b) => a.lo - b.lo);
    const stretches = [];
    for (const s of spans) {
      const last = stretches[stretches.length - 1];
      if (last && s.lo <= last.hi + ABUT) {
        last.hi = Math.max(last.hi, s.hi);
        last.depth = Math.max(last.depth, s.depth);
      } else stretches.push({ ...s });
    }
    stretches.forEach((s, k) => {
      runs.push({
        id: `${SHALLOW_RUN_ID_PREFIX}${wallId}${k ? `_${k}` : ''}`,
        source: 'auto',
        depth: s.depth,
        uppers: false,
        segments: [
          {
            wallId,
            kind: 'real',
            startParam: Math.max(0, s.lo),
            endParam: Math.min(axes.wallLen, s.hi),
          },
        ],
        bays: [],
      });
    });
  }
  return runs;
}

/**
 * Where along a stored segment's wall an object stands, read in the frame
 * the segment was written against (`wallFrame`). Null with no frame.
 */
function paramOnSegment(obj, seg) {
  const f = seg?.wallFrame;
  if (!f?.a || !f?.b || !obj?.center) return null;
  const len = Math.hypot(f.b.x - f.a.x, f.b.z - f.a.z);
  if (!(len > 0)) return null;
  return (
    ((obj.center.x - f.a.x) * (f.b.x - f.a.x) +
      (obj.center.z - f.a.z) * (f.b.z - f.a.z)) /
    len
  );
}

/**
 * Of the runs standing on one wall, the one a piece belongs to: a shallow
 * piece to a shallow run, any other to the wall's own run — and where one
 * wall holds several of the kind, the one whose segment the piece stands in.
 *
 * @param {Object} obj
 * @param {string} wallId
 * @param {Object[]} runsOnWall
 * @returns {string|undefined} the run's id
 */
function runForPieceOnWall(obj, wallId, runsOnWall) {
  const shallow = isShallowRunPiece(obj);
  const pool = (runsOnWall || []).filter(
    (r) => isShallowStorageRun(r) === shallow
  );
  if (pool.length <= 1) return pool[0]?.id;
  const hit = pool.find((r) =>
    (r.segments || []).some((seg) => {
      if (seg.wallId !== wallId) return false;
      const p = paramOnSegment(obj, seg);
      const lo = Math.min(seg.startParam, seg.endParam);
      const hi = Math.max(seg.startParam, seg.endParam);
      return p != null && p >= lo - ABUT && p <= hi + ABUT;
    })
  );
  return hit?.id;
}

/** The engine's pieces for a shallow run, marked as the generator's are. */
function markShallowPieces(objects, run) {
  if (!isShallowStorageRun(run)) return objects;
  return objects.map((o) =>
    o?.type === 'lower-cabinet'
      ? { ...o, category: { ...(o.category || {}), shallowRun: true } }
      : o
  );
}

module.exports = {
  SHALLOW_RUN_ID_PREFIX,
  isShallowRunPiece,
  isShallowStorageRun,
  shallowStorageRuns,
  runForPieceOnWall,
  markShallowPieces,
};
